import {Group} from '@visx/group';
import {GlyphSeries} from '@visx/xychart';
import React, {useCallback, useMemo, useState} from 'react';

import ChartTooltip, {
  LabelProps,
  TooltipInfo,
  useChartTooltip,
} from 'ui-library/charting/internals/ChartTooltip';
import {
  getColorForDataset,
  SeriesColorConfig,
} from 'ui-library/charting/internals/utils/getSeriesColorConfig';
import {
  AxisTypes,
  CartesianPoint as CartesianPointType,
  Dataset,
  isLabeledDataset,
} from 'ui-library/charting/Types';

import {useBenchlingChartContext} from '../BenchlingChartContext';
import CartesianPoint from './CartesianPoint';
import {getChartMaskId} from './ChartMask';
import ErrorBarSeries from './ErrorBarSeries';
import getCartesianAccessors from './utils/getCartesianAccessors';

export const NO_LABEL_SENTINEL = '__points';

export interface CartesianPointHoverInfo<DataPoint extends CartesianPointType> {
  dataPoint: DataPoint;
  dataPointKey: string;
  x: number;
  y: number;
}

export interface CartesianPointsProps<DataPoint extends CartesianPointType> {
  axisTypes: AxisTypes;
  colorConfig: SeriesColorConfig;
  dataset: Dataset<DataPoint>;
  errorValueLabel?: string;
  onClickPoint?: (dataPoint: DataPoint) => void;
  xAxisLabel: string;
  yAxisLabel: string;
}

export default function CartesianPointsWrapper<DataPoint extends CartesianPointType>(
  props: CartesianPointsProps<DataPoint>
): JSX.Element {
  return <CartesianPoints {...props} />;
}

export function getTooltipProps<DataPoint extends CartesianPointType>(
  hoverInfo: CartesianPointHoverInfo<DataPoint> | null,
  label?: LabelProps
): TooltipInfo<DataPoint> | null {
  return hoverInfo != null
    ? {
        left: hoverInfo.x,
        point: hoverInfo.dataPoint,
        top: hoverInfo.y,
        label,
        ...(hoverInfo.dataPoint.pointWarning != null && {
          warning: hoverInfo.dataPoint.pointWarning as string,
        }),
      }
    : null;
}

const CartesianPoints = React.memo(_CartesianPoints);

function _CartesianPoints<DataPoint extends CartesianPointType>({
  axisTypes,
  colorConfig,
  dataset,
  errorValueLabel,
  onClickPoint,
  xAxisLabel,
  yAxisLabel,
}: CartesianPointsProps<DataPoint>): JSX.Element {
  const {clientId} = useBenchlingChartContext();

  const color = getColorForDataset(dataset, colorConfig);
  const label = useMemo(
    () => (isLabeledDataset(dataset) ? {color, text: String(dataset.label)} : undefined),
    [color, dataset]
  );
  const dataAccessors = getCartesianAccessors(axisTypes);

  const {tooltipProps, updateTooltipProps} = useChartTooltip({
    axisTypes,
    columnLabels: {
      x: xAxisLabel,
      y: yAxisLabel,
      ...(axisTypes.errorValue == null ? {} : {errorValue: errorValueLabel}),
    },
    // 'errorValue' as 'errorValue' is needed for TypeScript to be happy
    columnNames: ['x', 'y', ...(axisTypes.errorValue == null ? [] : ['errorValue' as 'errorValue'])],
  });
  const [hoveredPointKey, setHoveredPointKey] = useState<string | null>(null);

  const handleOnHover = useCallback(
    (hoverInfo: CartesianPointHoverInfo<DataPoint> | null) => {
      setHoveredPointKey(hoverInfo?.dataPointKey ?? null);
      updateTooltipProps(getTooltipProps(hoverInfo, label));
    },
    [label, updateTooltipProps]
  );

  return (
    <>
      <Group mask={`url(#${getChartMaskId(clientId)})`}>
        <ErrorBarSeries
          color={color}
          data={dataset.points}
          label={label?.text}
          xAccessor={dataAccessors.xAccessor}
          yAccessor={dataAccessors.yAccessor}
        />
        <GlyphSeries
          dataKey={label?.text ?? NO_LABEL_SENTINEL}
          data={dataset.points}
          xAccessor={dataAccessors.xAccessor}
          yAccessor={dataAccessors.yAccessor}
          renderGlyph={({x, y, datum: dataPoint, key}) => (
            <CartesianPoint
              axisTypes={axisTypes}
              color={color}
              isHovered={key === hoveredPointKey}
              dataPoint={dataPoint}
              dataPointKey={key}
              onClick={onClickPoint}
              onHover={handleOnHover}
              x={x}
              y={y}
            />
          )}
        />
      </Group>
      {tooltipProps && <ChartTooltip {...tooltipProps} />}
    </>
  );
}
